import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import Dialog from '../../../base/ui/components/web/Dialog';
import Button from '../../../base/ui/components/web/Button';
import { readableText } from '../../../base/ui/readableText';
import useCaptionPreferenceSaveStatus from '../../useCaptionPreferenceSaveStatus';
// Direct-source import: captionPreferences consumes this flag internally and
// never re-exports it, so a named-import there resolved to undefined at
// runtime (transpile-only builds never caught it).
import { isCertifiedIpCtsEnabled } from '../../../base/whitelabel/certifiedMode';
import { getCaptionChoices } from '../../captionLocalization';
import { getCaptionPreferences, retryCaptionPreferenceSave, updateCaptionPreferences } from '../../captionPreferences';
const useStyles = makeStyles()(theme => ({
    retry: {
        color: readableText(theme.palette.text01, theme.palette.action01),
        minHeight: 44,
        marginBottom: 16,
        '&:hover': { color: readableText(theme.palette.text01, theme.palette.action01Hover) },
        '&:active': { color: readableText(theme.palette.text01, theme.palette.action01Active) }
    },
    certifiedNote: {
        color: theme.palette.text01,
        fontSize: 14,
        lineHeight: '21px',
        marginTop: 10
    },
    chip: {
        background: 'transparent',
        borderColor: theme.palette.ui05,
        borderRadius: 16,
        borderStyle: 'solid',
        borderWidth: 1,
        color: theme.palette.text01,
        cursor: 'pointer',
        fontSize: 14,
        lineHeight: '20px',
        minHeight: 44,
        minWidth: 44,
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        padding: '8px 12px',
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 }
    },
    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 14
    },
    chipSelected: {
        background: theme.palette.action01,
        borderColor: theme.palette.action01,
        color: '#ffffff'
    },
    choiceGroup: {
        border: 0,
        margin: 0,
        minWidth: 0,
        padding: 0
    },
    explanation: {
        color: theme.palette.text02,
        fontSize: 14,
        lineHeight: '21px',
        margin: '0 0 16px'
    },
    groupLabel: {
        color: theme.palette.text01,
        fontSize: 14,
        lineHeight: '20px',
        padding: 0,
        fontWeight: 700,
        marginBottom: 8
    }
}));
function ChoiceRow({ label, onChange, options, value }) {
    const { classes: styles } = useStyles();
    return (React.createElement("fieldset", { className: styles.choiceGroup },
        React.createElement("legend", { className: styles.groupLabel }, label),
        React.createElement("div", { className: styles.chipRow }, options.map(option => {
            const selected = option.value === value;
            return (React.createElement("button", { "aria-pressed": selected, className: selected ? `${styles.chip} ${styles.chipSelected}` : styles.chip, key: String(option.value), onClick: () => onChange(option.value), type: 'button' }, option.label));
        }))));
}
/**
 * In-conference caption preferences. Same store the client profile page and
 * mobile settings write; nothing here needs a room rejoin to take effect.
 * MalkaVRS and MalkaVRI captions are plain (Robert, 2026-11-01): size and hold
 * time only, no caption style or motion. Relay has its own Expressive style.
 */
const CaptionStyleDialog = () => {
    const { t } = useTranslation();
    const choices = getCaptionChoices(t);
    const saveStatus = useCaptionPreferenceSaveStatus();
    // Shared caption middleware refreshes this slice for same-app and browser
    // storage changes, so an open dialog reflects the current saved choices.
    useSelector((state) => state['features/subtitles']);
    const preferences = getCaptionPreferences();
    const patch = useCallback((part) => {
        updateCaptionPreferences(part);
    }, []);
    const { classes: styles } = useStyles();
    return (React.createElement(Dialog, { cancel: { hidden: true }, ok: { hidden: true }, title: t('captionSettings.title') },
        saveStatus !== 'idle' && saveStatus !== 'saved' && React.createElement("div", { "aria-live": 'polite', role: 'status' },
            React.createElement("p", { className: styles.explanation }, saveStatus === 'saving' ? 'Saving caption preferences…'
                : 'Save not confirmed. Try again.'),
            saveStatus === 'unconfirmed' && React.createElement(Button, { className: styles.retry, label: 'Retry saving caption preferences', onClick: retryCaptionPreferenceSave })),
        React.createElement(ChoiceRow, { label: t('captionSettings.sizeLabel'), onChange: value => patch({ captionSize: value }), options: choices.sizes, value: preferences.captionSize }),
        React.createElement(ChoiceRow, { label: t('captionSettings.dwellLabel'), onChange: value => patch({ captionDwellMs: value }), options: choices.dwell, value: preferences.captionDwellMs }),
        isCertifiedIpCtsEnabled() && (React.createElement("p", { className: styles.certifiedNote }, t('captionSettings.certifiedNote')))));
};
export default CaptionStyleDialog;
